import type { FirstPersonController } from "../player/FirstPersonController";
import {
  loadCrosshairConfig,
  subscribeCrosshair,
  type CrosshairConfig,
} from "../store/crosshairStore";

/**
 * Dynamic CS:GO-style crosshair (Sprint 3 + Sprint 6).
 *
 * Sprint 3 — the gap grows with the live inaccuracy cone from
 *   `FirstPersonController.getInaccuracyDeg()`.
 * Sprint 6 — every visual knob (color, thickness, length, gap, outline,
 *   style, dynamic-on-off) reads from the CrosshairConfig store. The
 *   CrosshairSettings panel writes to that store; this class subscribes
 *   and re-renders whenever the config changes.
 */
export class Crosshair {
  private readonly controller: FirstPersonController;
  private readonly root: HTMLElement;
  private readonly lineTop: HTMLDivElement;
  private readonly lineBottom: HTMLDivElement;
  private readonly lineLeft: HTMLDivElement;
  private readonly lineRight: HTMLDivElement;
  private readonly centerDot: HTMLDivElement;
  private rafHandle = 0;
  private cfg: CrosshairConfig;
  private unsubConfig: (() => void) | null = null;
  /** True while the crosshair is suppressed for an equipped sniper. */
  private hiddenForSniper = false;

  /** Degrees of inaccuracy per pixel of gap growth. */
  private static readonly DEG_PER_PX = 0.22;
  /** Hard cap on the dynamic gap (px) so lines don't fly off-screen on huge cones. */
  private static readonly MAX_DYNAMIC_GAP_PX = 28;

  constructor(controller: FirstPersonController) {
    this.controller = controller;
    this.cfg = loadCrosshairConfig();

    const existing = document.getElementById("crosshair");
    if (existing) {
      existing.innerHTML = "";
      existing.classList.add("crosshair-dynamic");
      this.root = existing;
    } else {
      this.root = document.createElement("div");
      this.root.id = "crosshair";
      this.root.classList.add("crosshair-dynamic");
      document.body.appendChild(this.root);
    }

    const mkLine = (cls: string) => {
      const el = document.createElement("div");
      el.className = `xh-line ${cls}`;
      this.root.appendChild(el);
      return el;
    };
    this.lineTop = mkLine("xh-top");
    this.lineBottom = mkLine("xh-bottom");
    this.lineLeft = mkLine("xh-left");
    this.lineRight = mkLine("xh-right");
    this.centerDot = document.createElement("div");
    this.centerDot.className = "xh-dot";
    this.root.appendChild(this.centerDot);

    this.injectStyles();
    this.applyConfig();
    this.unsubConfig = subscribeCrosshair((cfg) => {
      this.cfg = cfg;
      this.applyConfig();
    });
    this.rafHandle = requestAnimationFrame(this.tick);
  }

  destroy(): void {
    cancelAnimationFrame(this.rafHandle);
    this.unsubConfig?.();
    this.root.innerHTML = "";
    this.root.classList.remove("crosshair-dynamic");
  }

  private readonly tick = (): void => {
    this.render();
    this.rafHandle = requestAnimationFrame(this.tick);
  };

  /** Map inaccuracy (degrees) → gap delta (px). */
  private dynamicGap(deg: number): number {
    if (!this.cfg.dynamic) return 0;
    return Math.min(Crosshair.MAX_DYNAMIC_GAP_PX, deg / Crosshair.DEG_PER_PX);
  }

  /**
   * Recompute style-only properties (color, outline, line dimensions
   * that depend on `thickness`/`length`) whenever the config changes.
   * Per-frame positioning still happens inside `render()`.
   */
  private applyConfig(): void {
    const c = this.cfg;
    const outline = c.outline
      ? `0 0 0 ${c.outlineThickness}px ${c.outlineColor}, 0 0 1px rgba(0, 0, 0, 0.6)`
      : "0 0 1px rgba(0, 0, 0, 0.6)";

    // Hide / show pieces based on style.
    const showLines = c.style !== "dot";
    const showTopLine = showLines && c.style !== "t-shape"; // T-shape: drop the top line.
    const showDot = c.style === "dot" || c.style === "cross-dot";

    this.lineTop.style.display = showTopLine ? "block" : "none";
    this.lineBottom.style.display = showLines ? "block" : "none";
    this.lineLeft.style.display = showLines ? "block" : "none";
    this.lineRight.style.display = showLines ? "block" : "none";
    this.centerDot.style.display = showDot ? "block" : "none";

    for (const el of [this.lineTop, this.lineBottom, this.lineLeft, this.lineRight]) {
      el.style.background = c.color;
      el.style.boxShadow = outline;
    }
    this.centerDot.style.background = c.color;
    this.centerDot.style.boxShadow = outline;
    const dotSize = Math.max(1, c.thickness + 1);
    this.centerDot.style.width = `${dotSize}px`;
    this.centerDot.style.height = `${dotSize}px`;
    this.centerDot.style.top = `calc(50% - ${dotSize * 0.5}px)`;
    this.centerDot.style.left = `calc(50% - ${dotSize * 0.5}px)`;
  }

  private render(): void {
    // CS:GO — snipers get no hip crosshair: scoped aiming uses the
    // ScopeOverlay reticle, unscoped (noscope) is intentionally blind.
    const sniper = this.controller.isSniperEquipped();
    if (sniper !== this.hiddenForSniper) {
      this.hiddenForSniper = sniper;
      this.root.style.display = sniper ? "none" : "";
    }
    if (sniper) return;

    const inacc = this.controller.getInaccuracyDeg();
    const dyn = this.dynamicGap(inacc);
    const gap = this.cfg.gap + dyn;
    const len = this.cfg.length;
    const thick = this.cfg.thickness;
    const half = thick * 0.5;

    // Vertical lines: `thick` px wide, `len` px tall.
    this.lineTop.style.width = `${thick}px`;
    this.lineTop.style.height = `${len}px`;
    this.lineTop.style.top = `calc(50% - ${gap + len}px)`;
    this.lineTop.style.left = `calc(50% - ${half}px)`;

    this.lineBottom.style.width = `${thick}px`;
    this.lineBottom.style.height = `${len}px`;
    this.lineBottom.style.top = `calc(50% + ${gap}px)`;
    this.lineBottom.style.left = `calc(50% - ${half}px)`;

    this.lineLeft.style.height = `${thick}px`;
    this.lineLeft.style.width = `${len}px`;
    this.lineLeft.style.left = `calc(50% - ${gap + len}px)`;
    this.lineLeft.style.top = `calc(50% - ${half}px)`;

    this.lineRight.style.height = `${thick}px`;
    this.lineRight.style.width = `${len}px`;
    this.lineRight.style.left = `calc(50% + ${gap}px)`;
    this.lineRight.style.top = `calc(50% - ${half}px)`;
  }

  private injectStyles(): void {
    if (document.getElementById("crosshair-dynamic-styles")) return;
    const style = document.createElement("style");
    style.id = "crosshair-dynamic-styles";
    style.textContent = `
      #crosshair.crosshair-dynamic { width: 64px; height: 64px; }
      #crosshair.crosshair-dynamic::before,
      #crosshair.crosshair-dynamic::after { display: none; content: none; }
      #crosshair .xh-line,
      #crosshair .xh-dot {
        position: absolute;
        opacity: 0.92;
        transition: top 60ms linear, left 60ms linear, width 60ms linear, height 60ms linear;
      }
    `;
    document.head.appendChild(style);
  }
}
